import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  LoaderCircle,
  LockKeyhole
} from "lucide-react";
import type { ReactNode } from "react";

export const PRODUCT_LOGO_SRC = "/product-logo.svg";

const STEP_HINTS: Record<string, string> = {
  登录: "使用受邀账号完成身份验证。",
  授权: "核对应用与权限后再决定。",
  设置: "为账号设置登录密码。",
  回调: "正在完成安全验证。",
  登出: "正在清除此浏览器的会话。",
  会话: "正在处理当前会话。",
  完成: "账号已就绪。"
};

export function ProductMark({
  className = "brand-mark",
  decorative = true
}: {
  className?: string;
  decorative?: boolean;
}) {
  return (
    <img
      className={className}
      src={PRODUCT_LOGO_SRC}
      alt={decorative ? "" : "Matterline"}
      aria-hidden={decorative || undefined}
      draggable={false}
    />
  );
}

/**
 * Shared chrome for Auth Portal only. Must not import Ops Console shell/nav.
 */
export function IdentityShell({
  step,
  status,
  children
}: {
  step: string;
  status: string;
  children: ReactNode;
}) {
  const hint = STEP_HINTS[step];

  return (
    <div className="shell">
      <aside className="identity-aside" aria-label="身份门户">
        <div>
          <div className="brand">
            <ProductMark />
            <div className="brand-text">
              <strong>Matterline</strong>
              <span>身份入口</span>
            </div>
          </div>
          <p className="aside-lede">面向智能文档知识平台的安全入口。</p>
        </div>
        <div className="rail">
          <span className="rail-kicker">当前步骤</span>
          <span className="rail-step">{step}</span>
          <i aria-hidden="true" />
          <span className="rail-status">{status}</span>
          {hint ? <p className="rail-hint">{hint}</p> : null}
        </div>
        <small>
          <LockKeyhole size={12} aria-hidden="true" />
          受邀账号 · 加密传输
        </small>
      </aside>
      {children}
    </div>
  );
}

export type IdentityStatusVariant = "loading" | "success" | "failed";

export function IdentityStatus({
  title,
  message,
  variant = "loading",
  step = "会话",
  status,
  action
}: {
  title: string;
  message: string;
  variant?: IdentityStatusVariant;
  step?: string;
  status?: string;
  action?: { label: string; onClick: () => void; icon?: ReactNode };
}) {
  const resolvedStatus =
    status ??
    (variant === "failed" ? "需要处理" : variant === "success" ? "已完成" : "进行中");
  const role = variant === "failed" ? "alert" : "status";

  return (
    <IdentityShell step={step} status={resolvedStatus}>
      <main className="auth-main">
        <div className="status-card" role={role} aria-live="polite">
          <div className={`status-icon-well ${variant}`} aria-hidden="true">
            {variant === "failed" ? (
              <AlertCircle size={28} className="status-icon failed" />
            ) : variant === "success" ? (
              <CheckCircle2 size={28} className="status-icon success" />
            ) : (
              <LoaderCircle size={28} className="status-icon spin" />
            )}
          </div>
          <p className="eyebrow">
            {variant === "failed" ? "需要处理" : variant === "success" ? "完成" : "请稍候"}
          </p>
          <h1>{title}</h1>
          <p className="lede">{message}</p>
          {action ? (
            <button className="primary" type="button" onClick={action.onClick}>
              {action.icon}
              {action.label}
              <ArrowRight size={17} />
            </button>
          ) : null}
        </div>
      </main>
    </IdentityShell>
  );
}

export function FormError({ children }: { children: ReactNode }) {
  return (
    <div className="error" role="alert" aria-live="assertive">
      <AlertCircle size={16} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

export function AuthPanel({
  eyebrow,
  title,
  lede,
  children,
  footer
}: {
  eyebrow: string;
  title: string;
  lede: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <main className="auth-main">
      <div className="panel">
        <div className="panel-header">
          <ProductMark className="panel-mark" />
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p className="lede">{lede}</p>
        </div>
        <div className="panel-body">{children}</div>
        {footer ? <div className="panel-footer">{footer}</div> : null}
      </div>
    </main>
  );
}
